import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { A11yModule } from "@angular/cdk/a11y";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";

import swal from "sweetalert2";

import { VacacionesService } from "../vacaciones.service";
import {
  EstadoFilaPlan,
  FilaPlanVacaciones,
  PlanImportacionVacaciones,
  UsuarioPlan,
} from "../../shared/model/vacaciones/vacacion.model";

/** Cuántas veces se ha creado el componente: da un id único al título del modal. */
let instancias = 0;

/**
 * Carga del Excel de RH: el botón de subir, la vista previa y la carga.
 *
 * Dos pasos, como el formato FOS-005: primero se lee el archivo y se
 * enseña fila por fila qué se va a hacer, sin escribir nada; solo al
 * confirmar se guardan los saldos. El archivo se conserva porque al
 * confirmar viaja otra vez, junto con la huella de la previa.
 *
 * Desde el 24/09/2026 cada persona va a SU periodo de aniversario: el
 * vigente a la FECHA DE LA HOJA (el día al que RH la cuadró). Esa fecha
 * arranca con la que trae el libro y se puede corregir; la carga manda
 * exactamente la de la previa revisada. Lo mismo la HOJA del libro (si
 * trae varias con la tabla, se elige una) y las filas ASIGNADAS A MANO
 * (las que el nombre no alcanzó a cruzar solo): todo va en la huella.
 *
 * La carga es ADITIVA: nunca borra saldos ni gente que no venga en el
 * Excel. Quien no aparezca en la hoja se queda exactamente como está.
 *
 * Es compartido: lo usan Calendario → Configuración y Seguimiento RH, cada
 * uno con el botón al estilo de su barra. Al terminar emite `cargado` con
 * el plan aplicado, para que la pantalla vuelva a pedir sus datos.
 */
@Component({
  selector: "app-carga-excel-vacaciones",
  standalone: true,
  templateUrl: "./carga-excel-vacaciones.component.html",
  styleUrls: ["./carga-excel-vacaciones.component.css"],
  // A11yModule: cdkTrapFocus, para que Tab no se salga de la vista previa.
  imports: [CommonModule, FormsModule, A11yModule, MatButtonModule, MatTooltipModule],
})
export class CargaExcelVacacionesComponent implements OnDestroy {
  /**
   * Cómo se ve el botón de subir. "plano" es el de la barra de
   * Configuración del Calendario (.vac-btn--plano); "barra", el botón
   * secundario de la barra de una pantalla table-page (Seguimiento RH),
   * igual que su «Hoy» y sus «Exportar».
   */
  @Input() estilo: "plano" | "barra" = "plano";

  /** Se cargó el Excel. Lleva el plan que devolvió el servidor (lo aplicado). */
  @Output() cargado = new EventEmitter<PlanImportacionVacaciones>();

  /** El .xlsx elegido. Se conserva porque al confirmar se manda otra vez. */
  archivoExcel: File | null = null;
  nombreExcel = "";
  leyendoExcel = false;
  importando = false;

  /** Lo que se leyó del archivo. null = no hay vista previa abierta. */
  planImportacion: PlanImportacionVacaciones | null = null;

  /**
   * La FECHA DE LA HOJA que se ve en el campo (yyyy-MM-dd).
   *
   * Arranca con la que usó el servidor en la primera previa —la de cuando
   * se guardó el libro, que es también el HOY() con que el Excel sacó la
   * antigüedad— y si se cambia se vuelve a pedir la previa con ella.
   * Mientras no coincida con la de la previa que se ve (plan.fechaCorte)
   * no se puede cargar: se cargaría algo distinto de lo que se revisó.
   */
  fechaHoja = "";

  /**
   * La HOJA del libro que dice el selector. Igual que la fecha: si se
   * cambia se vuelve a pedir la previa, y mientras no sea la de la previa
   * que se ve (plan.hoja) no se puede cargar. Vacía mientras el libro
   * trae varias y todavía no se elige.
   */
  hojaElegida = "";

  /**
   * Las filas asignadas a mano: número de fila del Excel → idUsuario.
   *
   * Es lo que se PIDE: al llegar cada previa se vuelve a armar con lo que
   * el servidor de verdad aplicó (las filas MANUAL), así que una
   * asignación que rechazó (esa persona ya estaba en otra fila) se suelta
   * sola en vez de quedarse viajando. Mientras una previa viene en camino
   * lleva también lo recién pedido, para que la siguiente no lo pierda.
   */
  private asignaciones = new Map<number, number>();

  /**
   * Hoja y asignaciones TAL CUAL se mandaron en la previa que se ve. La
   * carga manda esto mismo (con la fecha de la previa): van en la huella,
   * y con otra cosa el servidor la rechazaría.
   */
  private pedidoDelPlan: { hoja: string | null; asignaciones: string | null } = {
    hoja: null,
    asignaciones: null,
  };

  /** Cuántas previas se han pedido: la respuesta de una vieja no pisa a la nueva. */
  private folioPrevia = 0;

  /** La espera antes de pedir la previa con la fecha nueva. */
  private esperaFechaHoja: ReturnType<typeof setTimeout> | null = null;

  /**
   * Cuántas filas hay de cada estado ("3 nuevas", "20 se actualizan"…),
   * para el resumen de arriba. Se arma una vez al recibir el plan y no en
   * cada ciclo, igual que las áreas.
   */
  conteoEstados: { estado: EstadoFilaPlan; texto: string }[] = [];

  /**
   * Los renglones del Excel que se van a cargar.
   *
   * Se guarda el NÚMERO DE FILA del Excel y no el índice del arreglo:
   * es lo que entiende el servidor y lo que se ve en la hoja, así que
   * al revisar se puede cotejar fila por fila.
   *
   * Existe porque un archivo puede traer varias áreas mezcladas —el de
   * 2026 se llama "DESARROLLO" y trae nueve— y se quiere cerrar una
   * primero. Es un Set para que la casilla de cada renglón se conteste
   * de inmediato: la plantilla lo pregunta una vez por fila en cada
   * ciclo.
   */
  seleccionadas = new Set<number>();

  /**
   * Las áreas del Excel con su conteo, ya calculadas.
   *
   * Se arman UNA vez al recibir el plan y no en cada ciclo: si la
   * plantilla recibiera un arreglo nuevo cada vez, *ngFor repintaría
   * las pastillas en cada detección de cambios.
   */
  private areasPlan: { clave: string; etiqueta: string; cargables: number }[] = [];

  /**
   * En qué fila quedó cada persona de COS (idUsuario → fila del Excel).
   * Se arma con el plan: al asignar a mano se avisa en el botón que esa
   * persona ya está en otra fila, que es lo que el servidor rechazaría.
   */
  private filaPorUsuario = new Map<number, number>();

  /* ---- El panel de "Asignar a…" (uno a la vez) ---- */
  /** La fila del Excel cuyo panel está abierto. null = ninguno. */
  asignandoFila: number | null = null;
  busquedaUsuario = "";
  /** Lo que casa con la búsqueda, ya recortado. Se arma al teclear, no en cada ciclo. */
  resultadosUsuario: UsuarioPlan[] = [];
  /** Cuántos más casan y no se enseñan. */
  masResultados = 0;
  private readonly TOPE_RESULTADOS = 8;

  /**
   * La fila cuyo «Asignar a…» recibe el foco cuando llegue la previa. Al
   * quitar una asignación, el botón que tenía el foco desaparece con la
   * previa nueva y el foco se iría al fondo de la página.
   */
  private focoTrasPrevia: number | null = null;

  /** El mismo tope que el resto de la app para lo que se sube. */
  private readonly MAX_EXCEL = 10 * 1024 * 1024;

  /** Tope del campo de fecha de la hoja: hoy. */
  readonly hoyIso = this.iso(new Date());

  /** Marca el tirón que da el modal cuando se hace clic afuera. */
  tiron = false;

  /** El id del título del modal (aria-labelledby), distinto en cada instancia. */
  readonly idTitulo = `vac-imp-titulo-${++instancias}`;

  constructor(
    private service: VacacionesService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  /** Si la pantalla se va (otra pestaña, otra ruta), lo que venga en camino ya no es de nadie. */
  ngOnDestroy(): void {
    this.folioPrevia++;
    if (this.esperaFechaHoja) {
      clearTimeout(this.esperaFechaHoja);
      this.esperaFechaHoja = null;
    }
  }

  /**
   * Se eligió un archivo: se pide la previa.
   *
   * Sin fecha: el servidor toma la de cuando se guardó el libro (o hoy,
   * si el libro no la trae). Ya no se manda el año del mes que se ve:
   * cada persona cae en su propio periodo. Sin hoja: el servidor toma la
   * única con la tabla, o pide elegir si hay varias.
   */
  eligeExcelRh(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    // Se limpia SIEMPRE: sin esto, volver a elegir el mismo archivo
    // después de corregirlo en Excel no dispara el evento otra vez.
    input.value = "";
    if (!archivo) {
      return;
    }
    if (!/\.xlsx$/i.test(archivo.name)) {
      this.aviso("El archivo tiene que ser un .xlsx de Excel.", "warning");
      return;
    }
    if (archivo.size > this.MAX_EXCEL) {
      this.aviso("El archivo pasa de 10 MB.", "warning");
      return;
    }

    this.olvidaExcel();
    this.archivoExcel = archivo;
    this.nombreExcel = archivo.name;
    this.pidePrevia(null);
  }

  /**
   * Pide la vista previa. La primera vez va sin fecha; después, con la
   * que se escribió en el campo. La hoja y las asignaciones salen de lo
   * que dicen el selector y los paneles. Si ya había una previa abierta
   * se queda a la vista mientras llega la nueva, y lo palomeado se
   * respeta.
   */
  private pidePrevia(fechaCorte: string | null): void {
    const archivo = this.archivoExcel;
    if (!archivo) {
      return;
    }
    const folio = ++this.folioPrevia;
    const anterior = this.planImportacion;
    const pedidoAnterior = this.pedidoDelPlan;
    const pedido = {
      hoja: this.hojaElegida || null,
      asignaciones: this.textoAsignaciones(this.asignaciones) || null,
    };
    this.leyendoExcel = true;
    this.cdr.detectChanges();

    this.service
      .previaImportacion(archivo, fechaCorte, pedido.hoja, pedido.asignaciones)
      .subscribe({
        next: (plan) => {
          if (folio !== this.folioPrevia) {
            return;
          }
          this.leyendoExcel = false;
          const foco = this.focoTrasPrevia;
          this.focoTrasPrevia = null;
          if (!plan || plan.codigoError) {
            this.previaFallida(
              (plan && plan.mensajeError) || "No se pudo leer el archivo.",
              anterior,
              pedidoAnterior,
            );
            return;
          }
          // Con otra fecha, un plan inválido y SIN filas no es otra lectura de
          // la hoja: el servidor rechazó la fecha (futura, fuera de rango) antes
          // de leerla y no trae codigoError. Si se guardara como plan, la tabla
          // desaparecería con «Hay que corregir el archivo» y, al corregir la
          // fecha, la selección volvería a la de por omisión: se desmarcarían
          // las ocultas palomeadas a mano y se remarcarían las filas quitadas.
          // Se trata como previa fallida, que conserva la anterior y lo palomeado.
          // (Lo mismo con otra hoja que no se pudo leer: se queda la que se veía.)
          if (anterior && plan.valido === false && !(plan.filas && plan.filas.length)) {
            this.previaFallida(
              this.errorDeOtraHoja(plan, pedido.hoja, anterior),
              anterior,
              pedidoAnterior,
            );
            return;
          }
          this.planImportacion = plan;
          this.pedidoDelPlan = pedido;
          // La que usó el servidor. Si no la pudo usar (sólo pasa en la primera
          // previa, que no manda fecha), la que trae el libro.
          this.fechaHoja = plan.fechaCorte || fechaCorte || plan.fechaExcel || "";
          this.hojaElegida = plan.hoja || "";
          // Lo que de verdad se asignó: una asignación rechazada no se queda.
          this.asignaciones = this.asignacionesDe(plan);
          this.preparaSeleccion(plan, anterior);
          this.cdr.detectChanges();
          // Tras asignar o quitar: si el botón que tenía el foco desapareció
          // con la previa nueva, el foco vuelve a la fila. Si ya está en otro
          // lado (la fecha, otra fila), no se le quita.
          const activo = document.activeElement;
          if (foco != null && (!activo || activo === document.body)) {
            this.enfocaAsignar(foco);
          }
        },
        error: (e) => {
          if (folio !== this.folioPrevia) {
            return;
          }
          this.leyendoExcel = false;
          this.focoTrasPrevia = null;
          this.previaFallida(
            e && e.status === 404
              ? "El servidor todavía no tiene la carga del Excel. Falta desplegar el backend."
              : "No se pudo leer el archivo. Revisa tu conexión.",
            anterior,
            pedidoAnterior,
          );
        },
      });
  }

  /**
   * La previa no llegó. Si era la primera no hay nada que enseñar y se
   * suelta el archivo; si era por cambiar la fecha, la hoja o una
   * asignación, se queda la previa que ya se había revisado, con su
   * fecha, su hoja y sus asignaciones.
   */
  private previaFallida(
    mensaje: string,
    anterior: PlanImportacionVacaciones | null,
    pedidoAnterior: { hoja: string | null; asignaciones: string | null },
  ): void {
    if (anterior) {
      this.planImportacion = anterior;
      this.pedidoDelPlan = pedidoAnterior;
      this.fechaHoja = anterior.fechaCorte || "";
      this.hojaElegida = anterior.hoja || "";
      this.asignaciones = this.asignacionesDe(anterior);
    } else {
      this.olvidaExcel();
    }
    this.aviso(mensaje, "error");
    this.cdr.detectChanges();
  }

  /**
   * El mensaje de una previa rechazada sin filas. Si se había pedido OTRA
   * hoja, dice cuál: los errores de columnas («La tabla no trae la columna
   * …») no la nombran, y como a la vista se queda la anterior parecería que
   * el problema es de la que se sigue viendo.
   */
  private errorDeOtraHoja(
    plan: PlanImportacionVacaciones,
    hojaPedida: string | null,
    anterior: PlanImportacionVacaciones,
  ): string {
    const errores = (plan.errores || []).join(" ");
    const otra = hojaPedida && hojaPedida !== (anterior.hoja || "") ? hojaPedida : null;
    if (!otra) {
      return errores || "No se pudo leer la hoja con esa fecha.";
    }
    const cual = errores.includes(otra) ? errores : `En la hoja «${otra}»: ${errores || "no se pudo leer."}`;
    return anterior.hoja ? `${cual} Se queda a la vista la hoja «${anterior.hoja}».` : cual;
  }

  /**
   * La previa que venga en camino ya no es de lo que dicen los controles.
   * Si se dejara llegar, cambiaría el plan a SU fecha (o su hoja) y
   * reescribiría el campo con ella: lo último que se tecleó se perdería
   * sin que nadie lo note.
   */
  private sueltaLoQueViene(): void {
    if (this.leyendoExcel) {
      this.folioPrevia++;
      this.leyendoExcel = false;
      this.cdr.detectChanges();
    }
  }

  /** Se deja de esperar a que se termine de escribir la fecha. */
  private cancelaEsperaFecha(): void {
    if (this.esperaFechaHoja) {
      clearTimeout(this.esperaFechaHoja);
      this.esperaFechaHoja = null;
    }
  }

  /**
   * Se cambió la fecha de la hoja: se vuelve a pedir la previa con ella.
   *
   * Se espera un momento a que se termine de escribir: el campo de fecha
   * avisa con cada dígito del año, y cada aviso sería una lectura del
   * archivo completo. Mientras tanto no se puede cargar.
   */
  cambiaFechaHoja(valor: string): void {
    this.fechaHoja = valor || "";
    this.cancelaEsperaFecha();
    // La previa que venga en camino ya no es de lo que dice el campo (por
    // ejemplo, al regresar a la fecha de la previa que ya se ve), y mientras
    // tanto la nota diría «Leyendo la hoja al…» con otra fecha.
    this.sueltaLoQueViene();
    const f = this.fechaHoja;
    // A medio escribir el campo da cadena vacía o un año de tres cifras.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f) || f < "2000-01-01") {
      return;
    }
    // El [max] del campo no impide TECLEAR una fecha futura, y el servidor la
    // rechaza sin leer la hoja. Se dice aquí y no se pide nada: la previa que
    // se ve se queda, y como el campo ya no coincide con ella no se puede
    // cargar hasta corregirla. El hoy se toma al momento y no de hoyIso (que
    // es de cuando se abrió la pantalla): con la pantalla abierta desde ayer,
    // la fecha de hoy saldría «futura».
    if (f > this.iso(new Date())) {
      this.aviso(
        "La fecha de la hoja no puede ser futura: es el día al que RH cuadró sus números.",
        "warning",
      );
      return;
    }
    // Ya es lo que se ve. Si la hoja o una asignación se quedaron a medio
    // pedir (su lectura se soltó arriba), sí se vuelve a pedir.
    if (this.coincideConLaPrevia(f)) {
      return;
    }
    this.esperaFechaHoja = setTimeout(() => {
      this.esperaFechaHoja = null;
      this.pidePrevia(f);
    }, 600);
  }

  /**
   * Con esa fecha, la previa que se ve ya es lo que dicen los controles:
   * la misma fecha, la misma hoja y las mismas asignaciones a mano.
   */
  private coincideConLaPrevia(fecha: string): boolean {
    const p = this.planImportacion;
    return (
      !!p &&
      fecha === p.fechaCorte &&
      !this.hojaPendiente &&
      this.textoAsignaciones(this.asignaciones) === this.textoAsignaciones(this.asignacionesDe(p))
    );
  }

  /** La fecha del campo ya no es la de la previa que se ve. */
  get fechaHojaPendiente(): boolean {
    const p = this.planImportacion;
    return !!p && this.fechaHoja !== (p.fechaCorte || "");
  }

  /**
   * La fecha con la que se vuelve a pedir la previa al cambiar la hoja o
   * una asignación: la del campo si ya es una fecha completa y no futura;
   * si no, la de la previa que se ve. Mientras se elige la hoja no se
   * manda ninguna, igual que en la primera lectura: el servidor pone la
   * del libro.
   */
  private fechaParaPedir(): string | null {
    if (this.eligiendoHoja) {
      return null;
    }
    const f = this.fechaHoja;
    if (/^\d{4}-\d{2}-\d{2}$/.test(f) && f >= "2000-01-01" && f <= this.iso(new Date())) {
      return f;
    }
    return (this.planImportacion && this.planImportacion.fechaCorte) || null;
  }

  /* ---------- La hoja del libro ----------
   *
   * RH sube cada área desde Seguimiento RH con el MISMO formato de hoja.
   * Si un libro trae varias hojas con la tabla (una por área, por
   * ejemplo), se carga UNA a la vez: el servidor no adivina cuál, y la
   * que se revisó es la que se carga. */

  /** Hay más de una hoja con la tabla: sale el selector. */
  get hayVariasHojas(): boolean {
    const p = this.planImportacion;
    return !!p && (p.hojas || []).length > 1;
  }

  /**
   * El libro trae varias hojas y todavía no se dice cuál: el servidor
   * contestó sin filas pidiendo elegir. Entonces el selector es lo
   * principal que se ve.
   */
  get eligiendoHoja(): boolean {
    const p = this.planImportacion;
    return !!p && this.hayVariasHojas && !p.hoja && !(p.filas || []).length;
  }

  /** La hoja del selector ya no es la de la previa que se ve. */
  get hojaPendiente(): boolean {
    const p = this.planImportacion;
    return !!p && (this.hojaElegida || "") !== (p.hoja || "");
  }

  /**
   * Se eligió otra hoja: se vuelve a pedir la previa con ella, sin espera
   * (un select no se "teclea"). Las asignaciones a mano son de las filas
   * de UNA hoja: con otra no significan nada y se sueltan; si se regresa a
   * la que se ve, vuelven las suyas.
   */
  cambiaHoja(valor: string): void {
    const p = this.planImportacion;
    if (!p || this.importando) {
      return;
    }
    this.hojaElegida = valor || "";
    this.cierraAsignar();
    this.cancelaEsperaFecha();
    const esLaQueSeVe = this.hojaElegida === (p.hoja || "");
    this.asignaciones = esLaQueSeVe ? this.asignacionesDe(p) : new Map<number, number>();
    if (!this.hojaElegida) {
      this.sueltaLoQueViene();
      return;
    }
    const fecha = this.fechaParaPedir();
    if (esLaQueSeVe && fecha === (p.fechaCorte || null)) {
      // Ya es lo que se ve: sólo se suelta lo que venía de la otra hoja.
      this.sueltaLoQueViene();
      return;
    }
    this.pidePrevia(fecha);
  }

  /** Suelta el archivo y el plan. No toca nada de lo que ya se guardó. */
  private olvidaExcel(): void {
    this.archivoExcel = null;
    this.nombreExcel = "";
    this.planImportacion = null;
    this.seleccionadas = new Set<number>();
    this.areasPlan = [];
    this.conteoEstados = [];
    this.fechaHoja = "";
    this.hojaElegida = "";
    this.asignaciones = new Map<number, number>();
    this.pedidoDelPlan = { hoja: null, asignaciones: null };
    this.filaPorUsuario = new Map<number, number>();
    this.cierraAsignar();
    this.focoTrasPrevia = null;
    // Lo que venga en camino ya no es de nadie.
    this.folioPrevia++;
    this.leyendoExcel = false;
    this.cancelaEsperaFecha();
  }

  /* ---------- Asignar una fila a mano ----------
   *
   * El nombre del Excel no siempre cruza solo: un apellido con otra
   * ortografía, un nombre de pila distinto o alguien que en COS está con
   * un solo apellido. En vez de corregir el Excel y volver a subirlo, se
   * dice aquí quién es en COS. Solo en las filas que no cruzaron EXACTO:
   * una exacta no se corrige a mano. La asignación viaja al servidor, que
   * vuelve a calcular la fila como si hubiera cruzado sola. */

  /** Las filas que se pueden asignar a mano (y a quién ofrecer). */
  puedeAsignarse(f: FilaPlanVacaciones): boolean {
    const p = this.planImportacion;
    if (!p || f.filaExcel == null || !(f.nombreExcel || "").trim()) {
      return false;
    }
    const c = f.confianza;
    if (c !== "SIN_RESOLVER" && c !== "APELLIDOS" && c !== "TOKENS" && c !== "MANUAL") {
      return false;
    }
    // Toda fila que se puede asignar trae su lista de parecidos, aunque sea
    // vacía. Sin lista es la que coincide con alguien de BAJA: ya se sabe
    // quién es y el servidor no la deja asignar a otro (su nota dice qué
    // hacer: reactivarlo o quitar la fila).
    if (f.candidatos == null) {
      return false;
    }
    // Sin nadie que ofrecer (un servidor anterior a esto no los manda) el
    // botón prometería algo que no se puede hacer.
    return !!((p.usuarios && p.usuarios.length) || (f.candidatos && f.candidatos.length));
  }

  /**
   * ¿Se puede asignar ahora? No mientras se lee OTRA hoja: la tabla todavía
   * enseña la anterior, y la asignación viajaría con la hoja nueva y el
   * número de fila de la vieja, que en la nueva es otra persona.
   */
  get asignarApagado(): boolean {
    return this.importando || this.hojaPendiente;
  }

  /** Abre (o cierra, si ya estaba abierto) el panel de una fila. */
  alternaAsignar(f: FilaPlanVacaciones): void {
    if (f.filaExcel == null) {
      return;
    }
    if (this.asignandoFila === f.filaExcel) {
      this.cierraAsignar(true);
      return;
    }
    if (this.asignarApagado) {
      return;
    }
    this.asignandoFila = f.filaExcel;
    this.busquedaUsuario = "";
    this.resultadosUsuario = [];
    this.masResultados = 0;
    // El panel se abre debajo de la fila; en las últimas de la caja
    // quedaba fuera de la vista. Se trae lo justo para verlo entero.
    this.cdr.detectChanges();
    const panel = this.host.nativeElement.querySelector<HTMLElement>(".vac-imp__asignar");
    if (panel && typeof panel.scrollIntoView === "function") {
      panel.scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Cierra el panel. Con `regresaFoco` (la ✕, Escape) el foco vuelve al
   * «Asignar a…» de la fila: el panel se destruye con lo que tenía el foco,
   * y sin esto se iría al fondo de la página, fuera del modal.
   */
  cierraAsignar(regresaFoco = false): void {
    const fila = this.asignandoFila;
    this.asignandoFila = null;
    this.busquedaUsuario = "";
    this.resultadosUsuario = [];
    this.masResultados = 0;
    if (regresaFoco && fila != null) {
      this.enfocaAsignar(fila);
    }
  }

  /** Pone el foco en el «Asignar a…» de esa fila, ya pintado. */
  private enfocaAsignar(fila: number): void {
    this.cdr.detectChanges();
    const b = this.host.nativeElement.querySelector<HTMLElement>(
      `.vac-imp__asignar-btn[data-fila="${fila}"]`,
    );
    if (b) {
      b.focus();
    }
  }

  /**
   * Escape dentro de la vista previa: si está abierto el panel de asignar,
   * cierra sólo ése; si no, lo mismo que la ✕. A media carga no hace nada.
   */
  alEscape(ev: Event): void {
    ev.stopPropagation();
    if (this.asignandoFila != null) {
      this.cierraAsignar(true);
      return;
    }
    this.cerrarPrevia();
  }

  /**
   * Busca entre todos los activos de COS. Cada palabra tiene que estar en
   * el nombre o en el usuario, sin importar acentos ni mayúsculas: "hdz
   * mendoza" no, pero "hernandez mend" sí.
   */
  buscaUsuario(texto: string): void {
    this.busquedaUsuario = texto || "";
    const palabras = this.normaliza(this.busquedaUsuario).split(" ").filter((x) => !!x);
    const usuarios = (this.planImportacion && this.planImportacion.usuarios) || [];
    if (!palabras.length) {
      this.resultadosUsuario = [];
      this.masResultados = 0;
      return;
    }
    const casan = usuarios.filter((u) => {
      const donde = this.normaliza(`${u.nombre || ""} ${u.userNom || ""}`);
      return palabras.every((x) => donde.includes(x));
    });
    this.resultadosUsuario = casan.slice(0, this.TOPE_RESULTADOS);
    this.masResultados = Math.max(0, casan.length - this.TOPE_RESULTADOS);
  }

  /**
   * En qué OTRA fila del plan quedó ya esa persona, o null. El servidor
   * no deja a una persona en dos filas: se avisa antes de pedirlo.
   */
  otraFilaDe(u: UsuarioPlan, f: FilaPlanVacaciones): number | null {
    const n = this.filaPorUsuario.get(u.idUsuario);
    return n != null && n !== f.filaExcel ? n : null;
  }

  /** Lo que dice el botón de una persona al pasar el cursor. */
  tipUsuario(u: UsuarioPlan, f: FilaPlanVacaciones): string {
    if (u.idUsuario === f.idUsuario) {
      return "Es a quien está asignada ahora";
    }
    const otra = this.otraFilaDe(u, f);
    if (otra != null) {
      return `Ya está en la fila ${otra}: una persona va en una sola fila`;
    }
    return `Asignar la fila ${f.filaExcel} a ${u.nombre || u.userNom || "esta persona"}`;
  }

  /** La fila es de esa persona: se vuelve a pedir la previa con la asignación. */
  asigna(f: FilaPlanVacaciones, u: UsuarioPlan): void {
    if (this.asignarApagado || f.filaExcel == null || !u || u.idUsuario == null) {
      return;
    }
    if (!this.puedeAsignarse(f) || u.idUsuario === f.idUsuario || this.otraFilaDe(u, f) != null) {
      return;
    }
    this.asignaciones.set(f.filaExcel, u.idUsuario);
    this.cierraAsignar();
    this.focoTrasPrevia = f.filaExcel;
    this.repide();
    // El panel ya no existe: mientras llega la previa, el foco espera en el
    // botón de la fila (la tabla de la previa anterior sigue a la vista).
    this.enfocaAsignar(f.filaExcel);
  }

  /** Quita la asignación a mano: la fila vuelve a como la cruzó el servidor. */
  quitaAsignacion(f: FilaPlanVacaciones): void {
    if (this.asignarApagado || f.filaExcel == null || !this.asignaciones.has(f.filaExcel)) {
      return;
    }
    this.asignaciones.delete(f.filaExcel);
    this.cierraAsignar();
    this.focoTrasPrevia = f.filaExcel;
    this.repide();
  }

  /**
   * Vuelve a pedir la previa con la misma hoja y la fecha del campo, por un
   * cambio de asignación. Si había una fecha esperando a terminar de
   * escribirse, esta lectura ya la lleva.
   */
  private repide(): void {
    this.cancelaEsperaFecha();
    this.pidePrevia(this.fechaParaPedir());
  }

  /** Las filas MANUAL del plan: lo que el servidor sí asignó. */
  private asignacionesDe(plan: PlanImportacionVacaciones | null): Map<number, number> {
    const m = new Map<number, number>();
    for (const f of (plan && plan.filas) || []) {
      if (f.confianza === "MANUAL" && f.filaExcel != null && f.idUsuario != null) {
        m.set(f.filaExcel, f.idUsuario);
      }
    }
    return m;
  }

  /** "12:505,30:214", ordenadas por fila. Vacía si no hay ninguna. */
  private textoAsignaciones(m: Map<number, number>): string {
    return Array.from(m.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([fila, id]) => `${fila}:${id}`)
      .join(",");
  }

  /** Sin acentos, en minúsculas y con un solo espacio entre palabras. */
  private normaliza(s: string): string {
    return (s || "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  /* ---------- Qué filas se cargan ----------
   *
   * El archivo trae más de lo que se quiere cargar hoy, y la columna
   * "Área" del Excel no alcanza para separarlo: hay filas que dicen
   * "LÍDERES" y filas con el área en blanco que igual son del equipo
   * que se está cerrando. Por eso se elige POR FILA y las áreas sólo
   * son un atajo para marcar de golpe. */

  /**
   * ¿Esta fila se puede cargar? Las OMITIDA no: el servidor ya decidió
   * que se quedan fuera (sin resolver, fila vacía, Alta IMSS posterior a
   * la fecha de la hoja…), y palomearlas prometería algo que no va a
   * pasar. Sin número de fila tampoco, porque es lo único que el
   * servidor sabe leer.
   */
  esCargable(f: FilaPlanVacaciones): boolean {
    return f.estado !== "OMITIDA" && f.filaExcel != null;
  }

  /**
   * ¿Se marca sola (al abrir, con "Todas" y con las pastillas de área)?
   * Las cargables, salvo las que vienen OCULTAS en el Excel: si RH las
   * escondió, casi siempre es gente que ya no va. Esas se palomean a
   * mano, una por una.
   */
  private seMarcaSola(f: FilaPlanVacaciones): boolean {
    return this.esCargable(f) && !f.oculta;
  }

  /**
   * Al abrir la previa se marca todo lo que se marca solo.
   *
   * Es el comportamiento de siempre —confirmar carga el archivo
   * entero— y quien quiera menos lo desmarca. Empezar en blanco
   * obligaría a palomear 38 renglones para hacer lo de todos los días.
   *
   * Si la previa es la misma hoja con otra fecha (o con otra asignación),
   * las filas que ya se podían palomear conservan lo que se decidió en
   * ellas, siempre que sigan siendo LA MISMA PERSONA: cambiar la fecha no
   * debe deshacer la selección, pero una fila que se acaba de asignar a
   * otra persona es otra decisión y arranca como cualquier fila nueva.
   * Con otra hoja nada se conserva: el número de fila ya es otra gente.
   */
  private preparaSeleccion(
    plan: PlanImportacionVacaciones | null,
    anterior?: PlanImportacionVacaciones | null,
  ): void {
    const filas = (plan && plan.filas) || [];
    const mismaHoja =
      !!anterior && (anterior.hoja || "") === ((plan && plan.hoja) || "");
    // Fila → a quién era, de las que ya se podían palomear.
    const conocidas = new Map<number, number | null>();
    for (const f of (mismaHoja && anterior && anterior.filas) || []) {
      if (this.esCargable(f)) {
        conocidas.set(f.filaExcel as number, f.idUsuario != null ? f.idUsuario : null);
      }
    }
    const marcadas = new Set<number>();
    const porUsuario = new Map<number, number>();
    // Las áreas se cuentan en el mismo recorrido, y en el orden en que
    // aparecen en la hoja: así las pastillas salen como el Excel.
    const areas = new Map<string, { clave: string; etiqueta: string; cargables: number }>();

    for (const f of filas) {
      if (this.esCargable(f)) {
        const n = f.filaExcel as number;
        const quien = f.idUsuario != null ? f.idUsuario : null;
        const mismaPersona = conocidas.has(n) && conocidas.get(n) === quien;
        const marcar = mismaPersona ? this.seleccionadas.has(n) : this.seMarcaSola(f);
        if (marcar) {
          marcadas.add(n);
        }
      }
      if (f.idUsuario != null && f.filaExcel != null && !porUsuario.has(f.idUsuario)) {
        porUsuario.set(f.idUsuario, f.filaExcel);
      }
      const clave = (f.area || "").trim();
      let a = areas.get(clave);
      if (!a) {
        // El área vacía también es un grupo: son siete filas del archivo
        // de 2026 y hay que poder marcarlas juntas.
        a = { clave, etiqueta: clave || "(sin área)", cargables: 0 };
        areas.set(clave, a);
      }
      // Lo que suma la pastilla del área: las que se marcan solas.
      if (this.seMarcaSola(f)) {
        a.cargables++;
      }
    }

    this.seleccionadas = marcadas;
    this.filaPorUsuario = porUsuario;
    this.areasPlan = Array.from(areas.values());
    this.conteoEstados = this.cuentaEstados(filas);
  }

  /** "3 nuevas · 20 se actualizan · 2 sin cambio · 4 omitidas", sin los ceros. */
  private cuentaEstados(filas: FilaPlanVacaciones[]): { estado: EstadoFilaPlan; texto: string }[] {
    const orden: EstadoFilaPlan[] = ["NUEVO", "ACTUALIZA", "IGUAL", "OMITIDA"];
    const r: { estado: EstadoFilaPlan; texto: string }[] = [];
    for (const e of orden) {
      const n = filas.filter((f) => f.estado === e).length;
      if (!n) {
        continue;
      }
      let texto: string;
      switch (e) {
        case "NUEVO":
          texto = n === 1 ? "nueva" : "nuevas";
          break;
        case "ACTUALIZA":
          texto = n === 1 ? "se actualiza" : "se actualizan";
          break;
        case "IGUAL":
          texto = "sin cambio";
          break;
        default:
          texto = n === 1 ? "omitida" : "omitidas";
      }
      r.push({ estado: e, texto: `${n} ${texto}` });
    }
    return r;
  }

  /** Las áreas distintas del Excel, con cuántas filas suma cada una. */
  areasDelPlan(): { clave: string; etiqueta: string; cargables: number }[] {
    return this.areasPlan;
  }

  /** ¿Está palomeada? La plantilla lo pregunta por cada renglón. */
  estaSeleccionada(fila?: number | null): boolean {
    return fila != null && this.seleccionadas.has(fila);
  }

  /**
   * Palomea o despalomea un renglón.
   *
   * Recibe el número de fila del Excel, no el índice: es lo que se ve
   * en la tabla y lo que viaja al servidor. Una fila que no se puede
   * cargar se ignora en vez de entrar al Set — si entrara, el botón
   * prometería cargar algo que el servidor va a omitir de todos modos.
   * Las ocultas en el Excel SÍ se pueden palomear aquí: es la única
   * forma de cargarlas.
   */
  alterna(fila?: number | null): void {
    if (fila == null || !this.filaCargablePorNumero(fila)) {
      return;
    }
    if (this.seleccionadas.has(fila)) {
      this.seleccionadas.delete(fila);
    } else {
      this.seleccionadas.add(fila);
    }
  }

  /** La fila del plan con ese número, sólo si es cargable. */
  private filaCargablePorNumero(fila: number): FilaPlanVacaciones | null {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    const f = filas.find((x) => x.filaExcel === fila);
    return f && this.esCargable(f) ? f : null;
  }

  /**
   * Marca todas las que se marcan solas. SUMA a lo marcado: una oculta que
   * ya se palomeó a mano no se desmarca.
   */
  seleccionaTodas(): void {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    for (const f of filas) {
      if (this.seMarcaSola(f)) {
        this.seleccionadas.add(f.filaExcel as number);
      }
    }
  }

  seleccionaNinguna(): void {
    this.seleccionadas = new Set<number>();
  }

  /**
   * Agrega un área a lo que ya está marcado. NO reemplaza la selección:
   * cerrar Desarrollo es marcar "DESARROLLO", después "LÍDERES" y
   * después "(sin área)", y si cada clic borrara lo anterior no se
   * podría armar la cohorte. Las ocultas del área no se suman solas.
   */
  seleccionaArea(area: string): void {
    for (const f of this.filasDeArea(area)) {
      if (this.seMarcaSola(f)) {
        this.seleccionadas.add(f.filaExcel as number);
      }
    }
  }

  /** Lo contrario: quita esa área de lo marcado (ocultas incluidas) y deja el resto intacto. */
  quitaArea(area: string): void {
    for (const f of this.filasDeArea(area)) {
      this.seleccionadas.delete(f.filaExcel as number);
    }
  }

  /** Las filas cargables de un área. El área vacía es el grupo "". */
  private filasDeArea(area: string): FilaPlanVacaciones[] {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    const clave = (area || "").trim();
    return filas.filter((f) => this.esCargable(f) && (f.area || "").trim() === clave);
  }

  /** Cuántas de esa área están marcadas, para apagar el botón de quitar. */
  seleccionadasDeArea(area: string): number {
    return this.filasDeArea(area).filter((f) => this.seleccionadas.has(f.filaExcel as number))
      .length;
  }

  /** Todas las filas del archivo que se PODRÍAN cargar. Es la M de "N de M". */
  get totalCargables(): number {
    return this.cuentaAplicables(this.planImportacion);
  }

  /** Las cargables ocultas en el Excel que nadie ha palomeado, para decirlo. */
  get ocultasSinMarcar(): number {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    return filas.filter(
      (f) => this.esCargable(f) && !!f.oculta && !this.seleccionadas.has(f.filaExcel as number),
    ).length;
  }

  /**
   * La casilla del encabezado: prendida si están todas las que se marcan
   * solas. Las ocultas no cuentan: "todas" es lo mismo que hace el botón.
   */
  get todasMarcadas(): boolean {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    let hay = false;
    for (const f of filas) {
      if (!this.seMarcaSola(f)) {
        continue;
      }
      hay = true;
      if (!this.seleccionadas.has(f.filaExcel as number)) {
        return false;
      }
    }
    return hay;
  }

  /** Ni todas ni ninguna: la casilla del encabezado sale en guion. */
  get marcadasAMedias(): boolean {
    return this.filasACargar > 0 && !this.todasMarcadas;
  }

  /** La casilla del encabezado. A medias cuenta como "faltan": marca todo. */
  alternaTodas(): void {
    if (this.todasMarcadas) {
      this.seleccionaNinguna();
    } else {
      this.seleccionaTodas();
    }
  }

  /** Lo que lee un lector de pantalla en la casilla de la fila. */
  rotuloCasilla(f: FilaPlanVacaciones): string {
    const quien = f.nombreExcel || f.nombreCos || `fila ${f.filaExcel}`;
    if (!this.esCargable(f)) {
      return `${quien}: no se puede cargar`;
    }
    return f.oculta ? `Cargar a ${quien} (oculta en el Excel)` : `Cargar a ${quien}`;
  }

  /** Los renglones marcados, ordenados como en la hoja, para el servidor. */
  private listaSeleccionadas(): number[] {
    const filas = (this.planImportacion && this.planImportacion.filas) || [];
    return filas
      .filter((f) => this.esCargable(f) && this.seleccionadas.has(f.filaExcel as number))
      .map((f) => f.filaExcel as number);
  }

  /** Cancelar la vista previa. A media carga no se cierra. */
  cerrarPrevia(): void {
    if (this.importando) {
      return;
    }
    this.olvidaExcel();
  }

  /**
   * Las filas que se van a aplicar: las PALOMEADAS.
   *
   * Se cuenta recorriendo el plan y no con el tamaño del Set para que
   * el número sea siempre el de lo que de verdad se puede cargar: si
   * quedara ahí un renglón que ya no existe o que salió OMITIDA, el
   * botón prometería una fila de más. Las que salen como "Sin cambio"
   * se vuelven a guardar idénticas: la carga es idempotente y no pasa
   * nada por repetirlas.
   */
  get filasACargar(): number {
    return this.listaSeleccionadas().length;
  }

  private cuentaAplicables(plan: PlanImportacionVacaciones | null): number {
    const filas = (plan && plan.filas) || [];
    return filas.filter((f) => f.estado !== "OMITIDA").length;
  }

  /** El rótulo del botón de confirmar. */
  get rotuloCargar(): string {
    if (this.importando) {
      return "Cargando…";
    }
    if (this.leyendoExcel) {
      return "Revisando la hoja…";
    }
    if (this.eligiendoHoja) {
      return "Elige la hoja";
    }
    // La fecha cambió y la previa todavía no es de esa fecha: cargar
    // ahora sería cargar algo distinto de lo que se ve.
    if (this.fechaHojaPendiente || this.esperaFechaHoja) {
      return "Revisa la fecha de la hoja";
    }
    const n = this.filasACargar;
    // Con el archivo lleno de filas cargables pero ninguna palomeada el
    // problema no es el archivo, es la selección: hay que decirlo así o
    // se busca el error en el Excel.
    if (!n) {
      return this.totalCargables ? "Elige al menos una fila" : "No hay nada que cargar";
    }
    return `Cargar ${n} ${n === 1 ? "fila" : "filas"}`;
  }

  /** Con un solo error el plan no es válido y no se puede cargar. */
  get puedeCargarExcel(): boolean {
    const p = this.planImportacion;
    return (
      !!p &&
      p.valido === true &&
      !!this.archivoExcel &&
      !this.importando &&
      !this.leyendoExcel &&
      // La carga manda la fecha de la previa revisada: tiene que existir y
      // ser la misma que dice el campo. Igual la hoja.
      !!p.fechaCorte &&
      !this.fechaHojaPendiente &&
      !this.esperaFechaHoja &&
      !this.hojaPendiente &&
      // Al menos una palomeada: sin esto se mandaría el archivo entero
      // justo cuando se pidió no cargar nada.
      this.filasACargar > 0
    );
  }

  cargarExcelRh(): void {
    const plan = this.planImportacion;
    const archivo = this.archivoExcel;
    if (!plan || !archivo || !this.puedeCargarExcel || !plan.fechaCorte) {
      return;
    }

    // La MISMA fecha de la previa que se revisó: con otra, cada persona
    // podría caer en otro periodo y se cargaría algo que nadie vio. Y la
    // misma hoja y las mismas asignaciones: van en la huella.
    const fecha = plan.fechaCorte;
    const pedido = this.pedidoDelPlan;
    // Los renglones palomeados viajan tal cual. El servidor escribe sólo
    // esos y devuelve el resto como OMITIDA, así que el conteo del aviso
    // sigue saliendo de la respuesta y no de lo que aquí se supuso.
    const filas = this.listaSeleccionadas();
    // De qué hoja, sólo si el libro trae varias: con una sola sobra.
    const hoja = this.hayVariasHojas ? plan.hoja : null;
    this.cierraAsignar();
    this.importando = true;
    this.cdr.detectChanges();

    this.service
      .importarExcel(archivo, fecha, pedido.hoja, pedido.asignaciones, plan.huella || "", filas)
      .subscribe({
        next: (res) => {
          this.importando = false;
          if (!res || res.codigoError) {
            this.aviso(
              (res && res.mensajeError) || "No se pudo cargar el archivo.",
              "error",
            );
            this.cdr.detectChanges();
            return;
          }
          const cargadas = this.cuentaAplicables(res);
          this.olvidaExcel();
          this.aviso(
            `Se cargaron ${cargadas} ${cargadas === 1 ? "persona" : "personas"} ` +
              (hoja ? `de la hoja «${hoja}»` : "con la hoja") +
              ` al ${this.fechaCompleta(fecha)}. ` +
              "Cada quien quedó en su periodo de aniversario.",
            "success",
          );
          this.cdr.detectChanges();
          // La pantalla vuelve a pedir lo suyo: tiene que enseñar los días,
          // los gozados y el periodo recién guardados.
          this.cargado.emit(res);
        },
        error: () => {
          this.importando = false;
          this.aviso("No se pudo cargar el archivo. Revisa tu conexión.", "error");
          this.cdr.detectChanges();
        },
      });
  }

  /* ---------- Cómo se ve cada fila de la previa ---------- */

  /**
   * La hoja dice otra cosa que COS en ese dato. Sólo si la hoja lo trae:
   * una columna vacía no es una diferencia, es que no la llenaron.
   */
  difiere(
    excel: number | string | null | undefined,
    cos: number | string | null | undefined,
  ): boolean {
    return excel != null && cos != null && excel !== cos;
  }

  /**
   * «Su periodo terminó el …; al cargar pasa a su periodo … con N
   * acumulados (hoy puede pedir M)». Es quien cumplió aniversario después
   * de que RH cuadró la hoja: lo que le sobró de ese periodo se le pasa al
   * nuevo como acumulado.
   */
  textoCierre(f: FilaPlanVacaciones): string {
    const acumulado = f.acumuladoNuevo != null ? f.acumuladoNuevo : 0;
    const hoy = f.disponiblesNuevo != null ? f.disponiblesNuevo : f.disponiblesDespues;
    return (
      `Su periodo terminó el ${this.fechaCompleta(f.periodoFin)}; al cargar pasa a su periodo ` +
      `del ${this.fechaCompleta(f.periodoNuevoInicio)} al ${this.fechaCompleta(f.periodoNuevoFin)} ` +
      `con ${acumulado} ${Math.abs(acumulado) === 1 ? "acumulado" : "acumulados"}` +
      (hoy != null ? ` (hoy puede pedir ${hoy})` : "") +
      "."
    );
  }

  /** Cómo se resolvió el nombre del Excel contra la gente de COS. */
  etiquetaConfianza(c?: string): string {
    switch (c) {
      case "EXACTA":
        return "Exacta";
      case "APELLIDOS":
        return "Por apellidos";
      case "TOKENS":
        return "Por palabras";
      case "MANUAL":
        return "Asignada a mano";
      default:
        return "Sin resolver";
    }
  }

  /** Qué le va a pasar a la fila. */
  etiquetaEstadoFila(e?: string): string {
    switch (e) {
      case "NUEVO":
        return "Nuevo";
      case "ACTUALIZA":
        return "Actualiza";
      case "IGUAL":
        return "Sin cambio";
      default:
        return "Omitida";
    }
  }

  trackFilaPlan(i: number, f: FilaPlanVacaciones): number {
    return f.filaExcel != null ? f.filaExcel : i;
  }

  trackUsuario(_: number, u: UsuarioPlan): number {
    return u.idUsuario;
  }

  /**
   * Clic en el fondo. No cierra: un clic de más perdería la revisión sin
   * aviso. El modal da un tirón corto que apunta a los botones del pie,
   * igual que los paneles del Calendario.
   */
  insisteEnCerrar(): void {
    if (this.tiron) {
      return; // ya esta sacudiendose; no encimar animaciones
    }
    this.tiron = true;
    setTimeout(() => {
      this.tiron = false;
      this.cdr.detectChanges();
    }, 380);
  }

  /** "2025-10-01" → "01/10/2025", como en la hoja de RH. Sin Date: un día no tiene hora. */
  fechaCompleta(iso?: string | null): string {
    if (!iso || iso.length < 10) {
      return "";
    }
    return `${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}`;
  }

  private iso(d: Date): string {
    const mes = `${d.getMonth() + 1}`.padStart(2, "0");
    const dia = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  private aviso(texto: string, icono: "success" | "error" | "warning"): void {
    swal.fire({
      text: texto,
      icon: icono,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#b71c1c",
    });
  }
}
